import { Transition } from '@headlessui/react'
import {
  CheckCircleIcon,
  CircleAlertIcon as ExclamationCircleIcon,
  InfoIcon as InformationCircleIcon,
  TriangleAlertIcon as ExclamationTriangleIcon,
  XIcon as XMarkIcon,
} from 'lucide-react'
import { Fragment } from 'react'

export type EmporionToastVariant = 'success' | 'error' | 'warning' | 'info' | 'loading'

type EmporionToastCardProps = {
  show: boolean
  variant: EmporionToastVariant
  title: string
  description?: string
  onClose: () => void
}

const variantClasses: Record<EmporionToastVariant, string> = {
  success: 'border-emerald-200 bg-emerald-50/95 text-emerald-900',
  error: 'border-rose-200 bg-rose-50/95 text-rose-900',
  warning: 'border-amber-200 bg-amber-50/95 text-amber-900',
  info: 'border-slate-200 bg-white text-slate-900',
  loading: 'border-slate-200 bg-white text-slate-900',
}

const Icon = ({ variant }: { variant: EmporionToastVariant }) => {
  if (variant === 'success') return <CheckCircleIcon className="h-[18px] w-[18px] text-emerald-600" />
  if (variant === 'error') return <ExclamationCircleIcon className="h-[18px] w-[18px] text-rose-600" />
  if (variant === 'warning') return <ExclamationTriangleIcon className="h-[18px] w-[18px] text-amber-600" />
  if (variant === 'loading') {
    return (
      <span
        className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-slate-300 border-t-slate-600"
        aria-hidden="true"
      />
    )
  }
  return <InformationCircleIcon className="h-[18px] w-[18px] text-slate-600" />
}

export const EmporionToastCard = ({
  show,
  variant,
  title,
  description,
  onClose,
}: EmporionToastCardProps) => (
  <Transition
    show={show}
    as={Fragment}
    appear
    enter="transition ease-out duration-180"
    enterFrom="opacity-0 translate-y-1.5 translate-x-1.5"
    enterTo="opacity-100 translate-y-0 translate-x-0"
    leave="transition ease-in duration-140"
    leaveFrom="opacity-100 translate-y-0 translate-x-0"
    leaveTo="opacity-0 translate-y-1 translate-x-1"
  >
    <div
      role={variant === 'error' || variant === 'warning' ? 'alert' : 'status'}
      aria-live={variant === 'error' || variant === 'warning' ? 'assertive' : 'polite'}
      className={`pointer-events-auto w-full max-w-sm rounded-lg border shadow-[0_10px_28px_rgba(15,23,42,0.08)] ${variantClasses[variant]}`}
    >
      <div className="flex items-start gap-2.5 p-3">
        <div className="mt-0.5 shrink-0">
          <Icon variant={variant} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold leading-5">{title}</p>
          {description ? (
            <p className="mt-0.5 text-xs leading-5 opacity-90">{description}</p>
          ) : null}
        </div>
        {variant !== 'loading' ? (
          <button
            type="button"
            onClick={onClose}
            className="rounded p-0.5 text-slate-500 transition hover:bg-slate-100 hover:text-slate-700"
            aria-label="Close notification"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        ) : null}
      </div>
    </div>
  </Transition>
)
